import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useToast } from '../components/common/Toast';
import { ArrowLeft, MapPin, Phone, AlertCircle, Clock, CheckCircle, AlertTriangle, MessageSquare } from 'lucide-react';
import api from '../services/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const InterventionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuxiliaire } = useAuth();
  const [intervention, setIntervention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [commentaire, setCommentaire] = useState('');
  const { showToast, ToastContainer } = useToast();

  useEffect(() => {
    loadIntervention();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadIntervention = async () => {
    try {
      const response = await api.get(`/interventions/${id}`);
      setIntervention(response.data);
      setCommentaire(response.data.commentaire || '');
    } catch (error) {
      showToast('Erreur chargement intervention', 'error');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmer = async (action) => {
    setActionLoading(true);
    try {
      await api.patch(`/interventions/${id}/confirmer`, { action });
      showToast(
        action === 'arrivee' ? 'Arrivée confirmée !' : 'Intervention effectuée !',
        'success'
      );
      loadIntervention();
    } catch (error) {
      showToast(error.response?.data?.message || 'Erreur', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSignaler = async (type) => {
    setActionLoading(true);
    try {
      await api.patch(`/interventions/${id}/signaler`, { type });
      showToast('Signalement envoyé aux coordinatrices', 'success');
    } catch (error) {
      showToast(error.response?.data?.message || 'Erreur', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveCommentaire = async () => {
    setActionLoading(true);
    try {
      await api.put(`/interventions/${id}`, { commentaire });
      showToast('Commentaire enregistré', 'success');
      loadIntervention();
    } catch (error) {
      showToast('Erreur enregistrement commentaire', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const callNumber = (number) => {
    window.location.href = `tel:${number}`;
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  const beneficiaire = intervention.beneficiaireId;

  const getVigilanceColor = (niveau) => {
    const colors = {
      vert: { bg: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      orange: { bg: 'bg-orange-500', badge: 'bg-orange-50 text-orange-700 border-orange-200' },
      rouge: { bg: 'bg-red-500', badge: 'bg-red-50 text-red-700 border-red-200' }
    };
    return colors[niveau] || colors.vert;
  };

  const vigilanceColor = getVigilanceColor(beneficiaire.niveauVigilance);

  return (
    <div className="pb-20 bg-gradient-to-br from-blue-50 via-white to-purple-50 min-h-screen">
      <ToastContainer />
      
      {/* Header moderne */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 text-white p-6 rounded-b-[2rem] shadow-xl sticky top-0 z-10">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 mb-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-3 py-2 rounded-xl transition-all active:scale-95"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Retour</span>
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold drop-shadow-sm">📋 Détails intervention</h1>
            <p className="text-blue-100 text-sm font-medium mt-1">
              {format(new Date(intervention.date), 'EEEE d MMMM yyyy', { locale: fr })}
            </p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
            <Clock size={28} className="text-white" />
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Horaires - Card premium */}
        <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-lg border border-blue-100 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-3">
                <Clock size={28} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Horaires prévus</p>
                <p className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {intervention.heureDebut} - {intervention.heureFin}
                </p>
              </div>
            </div>
            <div className={`px-3 py-2 rounded-xl ${vigilanceColor.badge} border-2 font-bold text-sm shadow-sm`}>
              {beneficiaire.niveauVigilance.toUpperCase()}
            </div>
          </div>
        </div>

        {/* Bénéficiaire - Card moderne */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl p-2">
              <MapPin size={20} className="text-white" />
            </div>
            <h2 className="text-lg font-bold text-gray-800">Bénéficiaire</h2>
          </div>
          
          <div className="space-y-3">
            <p className="text-xl font-bold text-gray-800">
              {beneficiaire.prenom} {beneficiaire.nom}
            </p>
            
            <div className="bg-gray-50 rounded-xl p-3 flex items-start gap-2">
              <MapPin size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-700 font-medium">
                <p>{beneficiaire.adresse.rue}</p>
                <p>{beneficiaire.adresse.codePostal} {beneficiaire.adresse.ville}</p>
              </div>
            </div>

            {beneficiaire.telephone && (
              <button
                onClick={() => callNumber(beneficiaire.telephone)}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95"
              >
                <Phone size={20} />
                Appeler bénéficiaire
              </button>
            )}
          </div>
        </div>

        {/* Plages horaires autorisées */}
        {beneficiaire.plagesHoraires && beneficiaire.plagesHoraires.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Clock size={18} className="text-blue-500" />
              Plages horaires autorisées
            </h3>
            <div className="text-sm">
              {(() => {
                const jourIntervention = new Date(intervention.date).getDay();
                const plagesDuJour = beneficiaire.plagesHoraires.filter(p => p.jourSemaine === jourIntervention);
                
                if (plagesDuJour.length === 0) {
                  return <p className="text-gray-500 bg-gray-50 rounded-lg p-3">Pas de plage pour ce jour</p>;
                }
                
                return (
                  <div className="flex flex-wrap gap-2">
                    {plagesDuJour.map((plage, idx) => (
                      <span key={idx} className="bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 px-4 py-2 rounded-xl font-bold border border-blue-200 shadow-sm">
                        {plage.heureDebut} - {plage.heureFin}
                      </span>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Consignes importantes */}
        {beneficiaire.consignes && (
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-2xl shadow-lg p-5">
            <div className="flex gap-3">
              <div className="bg-amber-400 rounded-full p-2 h-fit">
                <AlertCircle size={24} className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                  ⚠️ Consignes importantes
                </h3>
                <p className="text-sm text-amber-800 leading-relaxed">{beneficiaire.consignes}</p>
              </div>
            </div>
          </div>
        )}

        {/* Contact urgence */}
        {beneficiaire.contactUrgence?.telephone && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-300 rounded-2xl shadow-lg p-5">
            <h3 className="font-bold text-red-800 mb-3 flex items-center gap-2">
              🚨 Contact d'urgence
            </h3>
            <p className="text-sm text-red-700 mb-3 font-medium">
              {beneficiaire.contactUrgence.nom}
              {beneficiaire.contactUrgence.lien && ` (${beneficiaire.contactUrgence.lien})`}
            </p>
            <button
              onClick={() => callNumber(beneficiaire.contactUrgence.telephone)}
              className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95"
            >
              <Phone size={20} />
              Appeler en urgence
            </button>
          </div>
        )}

        {/* Type intervention */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
          <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
            📝 Type d'intervention
          </h3>
          <p className="text-gray-700 bg-gray-50 rounded-lg p-3 font-medium">{intervention.type}</p>
        </div>

        {/* Actions pour auxiliaires */}
        {isAuxiliaire && intervention.statut !== 'effectuee' && intervention.statut !== 'annulee' && (
          <div className="space-y-4">
            {(() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const interventionDate = new Date(intervention.date);
              interventionDate.setHours(0, 0, 0, 0);
              const isPast = interventionDate < today;
              const isFuture = interventionDate > today;

              if (isFuture) {
                return (
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-300 rounded-2xl shadow-lg p-5 text-center">
                    <div className="bg-blue-400 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                      <Clock size={32} className="text-white" />
                    </div>
                    <p className="text-sm text-blue-800 font-medium leading-relaxed">
                      ℹ️ Cette intervention est prévue pour le {format(new Date(intervention.date), 'dd/MM/yyyy', { locale: fr })}. 
                      <br />Vous pourrez la traiter le jour prévu.
                    </p>
                  </div>
                );
              }

              if (isPast && intervention.statut === 'planifiee') {
                return (
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-300 rounded-2xl shadow-lg p-5 text-center">
                    <div className="bg-orange-400 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                      <AlertTriangle size={32} className="text-white" />
                    </div>
                    <p className="text-sm text-orange-800 font-medium leading-relaxed">
                      ⚠️ Cette intervention est passée. Seule une coordinatrice peut la clôturer.
                      <br />Contactez votre responsable.
                    </p>
                  </div>
                );
              }

              return (
                <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-lg border border-blue-100 p-5">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    ⚡ Actions rapides
                  </h3>
                  <div className="space-y-3">
                    {intervention.statut === 'planifiee' && (
                      <>
                        <button
                          disabled={actionLoading}
                          onClick={() => handleConfirmer('arrivee')}
                          className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95"
                        >
                          <CheckCircle size={22} />
                          Je suis arrivée
                        </button>
                        <button
                          disabled={actionLoading}
                          onClick={() => handleSignaler('retard')}
                          className="w-full bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95"
                        >
                          <Clock size={22} />
                          Signaler un retard
                        </button>
                        <button
                          disabled={actionLoading}
                          onClick={() => handleSignaler('empechement')}
                          className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95"
                        >
                          <AlertTriangle size={22} />
                          Signaler un empêchement
                        </button>
                      </>
                    )}

                    {intervention.statut === 'arrivee' && (
                      <button
                        disabled={actionLoading}
                        onClick={() => handleConfirmer('effectuee')}
                        className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95"
                      >
                        <CheckCircle size={22} />
                        Intervention effectuée
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Commentaire */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
              <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <MessageSquare size={20} className="text-purple-500" />
                Commentaire (optionnel)
              </h3>
              <textarea
                value={commentaire}
                onChange={(e) => setCommentaire(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px] font-medium"
                placeholder="Ajoutez un commentaire sur l'intervention..."
              />
              <div className="mt-3 space-y-2">
                <p className="text-xs text-gray-600 font-medium">💡 Suggestions rapides :</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setCommentaire('✅ Tout s\'est bien passé')}
                    className="px-3 py-2 bg-green-100 text-green-700 rounded-xl text-xs font-bold hover:bg-green-200 transition-all active:scale-95"
                  >
                    ✅ Tout OK
                  </button>
                  <button
                    onClick={() => setCommentaire('⚠️ Point à surveiller')}
                    className="px-3 py-2 bg-yellow-100 text-yellow-700 rounded-xl text-xs font-bold hover:bg-yellow-200 transition-all active:scale-95"
                  >
                    ⚠️ À surveiller
                  </button>
                  <button
                    onClick={() => setCommentaire('🚨 Problème signalé')}
                    className="px-3 py-2 bg-red-100 text-red-700 rounded-xl text-xs font-bold hover:bg-red-200 transition-all active:scale-95"
                  >
                    🚨 Problème
                  </button>
                </div>
              </div>
              <button
                disabled={actionLoading || !commentaire.trim()}
                onClick={handleSaveCommentaire}
                className="w-full mt-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-3 rounded-xl shadow-lg transition-all active:scale-95"
              >
                Enregistrer commentaire
              </button>
            </div>
          </div>
        )}

        {/* Statut effectuée */}
        {intervention.statut === 'effectuee' && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl shadow-lg p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-green-500 rounded-full p-2">
                <CheckCircle size={24} className="text-white" />
              </div>
              <h3 className="font-bold text-green-800 text-lg">✓ Intervention effectuée</h3>
            </div>
            {intervention.commentaire && (
              <div className="bg-white rounded-xl p-3 mt-3">
                <p className="text-sm font-bold text-gray-700 mb-1">💬 Commentaire :</p>
                <p className="text-sm text-gray-800">{intervention.commentaire}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InterventionDetailPage;
